import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LocationMap = ({ vendor }) => {
  const [showFullMap, setShowFullMap] = useState(false);

  const serviceAreas = [
    { city: 'San Francisco', distance: '5 miles', isMain: true },
    { city: 'Oakland', distance: '15 miles', isMain: false },
    { city: 'San Jose', distance: '25 miles', isMain: false },
    { city: 'Berkeley', distance: '12 miles', isMain: false },
    { city: 'Fremont', distance: '20 miles', isMain: false },
    { city: 'Palo Alto', distance: '18 miles', isMain: false }
  ];

  return (
    <div className="bg-white rounded-xl border border-border shadow-soft">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Location & Service Areas
          </h3>
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconSize={16}
            onClick={() => setShowFullMap(true)}
          >
            View Full Map
          </Button>
        </div>

        {/* Main Location */}
        <div className="mb-6">
          <div className="flex items-start space-x-3 mb-4">
            <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
              <Icon name="MapPin" size={20} color="#8B4513" />
            </div>
            <div>
              <div className="font-medium text-text-primary">{vendor.businessAddress}</div>
              <div className="text-sm text-text-secondary font-caption">
                {vendor.location} • Primary Service Area
              </div>
              <div className="flex items-center space-x-4 mt-2 text-sm">
                <div className="flex items-center space-x-1">
                  <Icon name="Clock" size={14} color="#6B5B4F" />
                  <span className="text-text-secondary font-caption">
                    Response time: {vendor.responseTime}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Car" size={14} color="#6B5B4F" />
                  <span className="text-text-secondary font-caption">
                    Travel radius: {vendor.serviceRadius} miles
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Embedded Map */}
        <div className="mb-6">
          <div className="relative h-64 bg-surface-100 rounded-lg overflow-hidden">
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title={`${vendor.name} Location`}
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${vendor.coordinates.lat},${vendor.coordinates.lng}&z=12&output=embed`}
              className="border-0"
            />
            
            {/* Map Overlay */}
            <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-soft">
              <div className="font-medium text-text-primary text-sm">{vendor.name}</div>
              <div className="text-xs text-text-secondary font-caption">{vendor.location}</div>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="mb-6">
          <h4 className="font-medium text-text-primary mb-3">Service Areas</h4>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {serviceAreas.map((area) => (
              <div
                key={area.city}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  area.isMain 
                    ? 'bg-primary-50 border-primary-200' :'bg-surface-50 border-border'
                }`}
              >
                <div>
                  <div className={`font-medium text-sm ${
                    area.isMain ? 'text-primary' : 'text-text-primary'
                  }`}>
                    {area.city}
                  </div>
                  <div className="text-xs text-text-secondary font-caption">
                    {area.distance} away
                  </div>
                </div>
                {area.isMain && (
                  <Icon name="Star" size={14} color="#8B4513" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Travel Information */}
        <div className="bg-surface-50 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} color="#6B5B4F" className="mt-0.5" />
            <div className="text-sm text-text-secondary font-caption">
              <div className="font-medium text-text-primary mb-1">Travel Policy:</div>
              <ul className="space-y-1">
                <li>• Free travel within 25-mile radius</li>
                <li>• $2 per mile for distances beyond 25 miles</li>
                <li>• Overnight accommodation required for 100+ mile events</li>
                <li>• Destination weddings welcome with advance notice</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="md"
            iconName="Navigation"
            iconSize={16}
            fullWidth
          >
            Get Directions
          </Button>
          <Button
            variant="secondary"
            size="md"
            iconName="Phone"
            iconSize={16}
            fullWidth
          >
            Call Now
          </Button>
          <Button
            variant="primary"
            size="md"
            iconName="MessageCircle"
            iconSize={16}
            fullWidth
          >
            Message
          </Button>
        </div>

        {/* Nearby Landmarks */}
        <div className="mt-6">
          <h4 className="font-medium text-text-primary mb-3">Nearby Landmarks</h4>
          <div className="space-y-2">
            {vendor.nearbyLandmarks.map((landmark, index) => (
              <div key={index} className="flex items-center space-x-3 text-sm">
                <Icon name="MapPin" size={14} color="#6B5B4F" />
                <span className="text-text-primary">{landmark.name}</span>
                <span className="text-text-secondary font-caption">
                  {landmark.distance} • {landmark.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full Map Modal */}
      {showFullMap && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-full flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-semibold text-lg text-text-primary">
                {vendor.name} - Service Area Map
              </h3>
              <button
                onClick={() => setShowFullMap(false)}
                className="w-8 h-8 rounded-lg hover:bg-surface-100 flex items-center justify-center transition-smooth"
              >
                <Icon name="X" size={20} color="#6B5B4F" />
              </button>
            </div>
            
            <div className="flex-1 p-4">
              <div className="h-96 bg-surface-100 rounded-lg overflow-hidden">
                <iframe
                  width="100%"
                  height="100%"
                  loading="lazy"
                  title={`${vendor.name} Full Map`}
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${vendor.coordinates.lat},${vendor.coordinates.lng}&z=10&output=embed`}
                  className="border-0"
                />
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-text-primary mb-2">Primary Service Areas</h4>
                  <div className="space-y-1">
                    {serviceAreas.filter(area => area.isMain || parseFloat(area.distance) <= 25).map((area) => (
                      <div key={area.city} className="flex items-center justify-between text-sm">
                        <span className="text-text-primary">{area.city}</span>
                        <span className="text-text-secondary font-caption">{area.distance}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-text-primary mb-2">Extended Service Areas</h4>
                  <div className="space-y-1">
                    {serviceAreas.filter(area => !area.isMain && parseFloat(area.distance) > 25).map((area) => (
                      <div key={area.city} className="flex items-center justify-between text-sm">
                        <span className="text-text-primary">{area.city}</span>
                        <span className="text-text-secondary font-caption">{area.distance} (+travel fee)</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationMap;